// @flow

import React, { Component } from 'react'
import Select, { components } from 'react-select'
import withTheme from 'hoc/withTheme'

import cn from 'classnames'

import styles from './Dropdown.module.scss'

import supportedThemes from '../themes/__supportedThemes.js'

type Props = {
    required?: boolean,
    disabled?: boolean,
    placeholder: ?string,
    options: Array<string> | { [value: string]: string },
    value?: string,
    defaultValue?: string,
    onChange: Function,
    trans?: string => string,
    optionFormat?: string,
    styles?: object,
};

class Dropdown extends Component<Props> {
    static defaultProps = {
        required: true,
        disabled: false,
        placeholder: 'Select',
        optionFormat: '%name%',
        styles: {},
    }

    constructor (props) {
        super(props)
        this.select = React.createRef()
    }

    get options (): { [string]: string } {
        if (!Array.isArray(this.props.options)) {
            return this.props.options
        }

        let options = {}

        this.props.options.forEach(item => {
            options[item] = this.formatOptionLabel(item)
        })

        return options
    }

    formatOptionLabel (label: string): string {
        label = this.props.optionFormat.replace('%name%', label)

        if (!this.props.trans) {
            return label
        }

        return this.props.trans(label)
    }

    render () {
        let {
            placeholder,
            value,
            defaultValue,
            onChange,
            theme
        }: Props = this.props

        let options = this.options
        let valueProp

        if (value) {
            valueProp = { value }
        } else {
            if (defaultValue) {
                valueProp = { defaultValue }
            } else {
                valueProp = { value: '' }
            }
        }

        const selectOptions = [
            placeholder ? { value: '', label: placeholder } : {},
            ...Object.keys(options).map(key => ({ value: key, label: options[key] }))
        ]

        return (
            <div className={cn(styles.dropdown, this.props.className)} style={{
                '--dropdown-color': theme.dropdownColor,
                '--field-background': theme.fieldBackground,
                ...this.props.styles,
            }}>
                <Select
                    {...valueProp}
                    components={{ DropdownIndicator }}
                    className="sh-select__container"
                    classNamePrefix="sh-select"
                    ref={this.select}
                    placeholder={placeholder || null}
                    required={this.props.required}
                    isDisabled={this.props.disabled}
                    isSearchable={false}
                    onFocus={this.props.onFocus || null}
                    onBlur={this.props.onBlur || null}
                    options={selectOptions}
                    onChange={selectedOption => onChange(selectedOption.value)}
                    defaultValue={selectOptions.find(op => op.value === this.props.defaultValue) || null}
                />
                <input
                    tabIndex={-1}
                    value={this.props.value || this.props.defaultValue}
                    required={this.props.required}
                    onChange={selectedOption => onChange(selectedOption.value)}
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        background: 'transparent',
                        border: 0,
                        pointerEvents: 'none',
                        color: 'transparent',
                        textIndent: '-9999px'
                    }}
                    onFocus={() => {
                        const controlRef = this.select.current.select?.controlRef
                        if (controlRef) {
                            controlRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                        this.select.current.focus()
                    }}
                />
            </div>
        )
    }
}

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <ArrowDown width={`${14 / 16}em`} height={`${10 / 16}em`} />
        </components.DropdownIndicator>
    )
}

const ArrowDown = ({ width, height, color }) => (
    <svg width="20"
        height="20"
        viewBox="0 0 20 20"
        version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
        <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
            <g>
                <rect
                    width="19"
                    height="19"
                    x="0.5"
                    y="0.5"
                    stroke={color || '#29BAE5'}
                    rx="9.5"
          ></rect>
                <path
                    fill={color || '#29BAE5'}
                    d="M10.416 8.624l3.066 4.599a.5.5 0 01-.416.777H6.934a.5.5 0 01-.416-.777l3.066-4.599a.5.5 0 01.832 0z"
                    transform="matrix(1 0 0 -1 0 22)"
          ></path>
            </g>
        </g>
    </svg>
)

export default withTheme(supportedThemes)(Dropdown)
